import { Controller } from "@hotwired/stimulus"

/**
 * Base date picker controller — shared between POS and public booking flows.
 *
 * Subclasses only need to exist to register under their own Stimulus identifier
 * (e.g. "pos-date-picker", "public-date-picker"). Override `actionIdentifier`
 * to match.
 *
 * Values:
 *   initialDates (Array)         — pre-selected date strings (YYYY-MM-DD)
 *   hiddenInputName (String)     — name attr for hidden inputs (default: "booking[dive_dates][]")
 */
export default class extends Controller {
  static targets = ["monthLabel", "daysGrid", "chips", "hiddenInputs"]
  static values = {
    initialDates: { type: Array, default: [] },
    hiddenInputName: { type: String, default: "booking[dive_dates][]" }
  }

  connect() {
    this.selectedDates = new Set(this.initialDatesValue)
    this.today = new Date()
    this.today.setHours(0, 0, 0, 0)

    // If there are preselected dates, navigate to the earliest date's month
    if (this.selectedDates.size > 0) {
      const earliest = Array.from(this.selectedDates).sort()[0]
      const d = new Date(earliest + "T00:00:00")
      this.viewYear = d.getFullYear()
      this.viewMonth = d.getMonth()
    } else {
      this.viewYear = this.today.getFullYear()
      this.viewMonth = this.today.getMonth()
    }

    this.renderCalendar()
    this.renderChips()
    this.syncHiddenInputs()
  }

  /**
   * The Stimulus identifier used in data-action attributes.
   * Override in subclass to match its registered name.
   */
  get actionIdentifier() {
    return this.identifier
  }

  prevMonth(event) {
    event.preventDefault()
    if (this.viewMonth === 0) {
      this.viewMonth = 11
      this.viewYear--
    } else {
      this.viewMonth--
    }
    this.renderCalendar()
  }

  nextMonth(event) {
    event.preventDefault()
    if (this.viewMonth === 11) {
      this.viewMonth = 0
      this.viewYear++
    } else {
      this.viewMonth++
    }
    this.renderCalendar()
  }

  toggleDate(event) {
    event.preventDefault()
    const dateStr = event.currentTarget.dataset.date
    if (!dateStr) return
    const selected = !this.selectedDates.has(dateStr)
    if (selected) {
      this.selectedDates.add(dateStr)
    } else {
      this.selectedDates.delete(dateStr)
    }
    this.renderCalendar()
    this.renderChips()
    this.syncHiddenInputs()
    this.dispatch("dateToggled", { detail: { date: dateStr, selected } })
  }

  removeDate(event) {
    event.preventDefault()
    const dateStr = event.currentTarget.dataset.date
    this.selectedDates.delete(dateStr)
    this.renderCalendar()
    this.renderChips()
    this.syncHiddenInputs()
    this.dispatch("dateToggled", { detail: { date: dateStr, selected: false } })
  }

  // Called externally (e.g. by dives controller) to deselect a date
  deselectDate(dateStr) {
    if (!this.selectedDates.has(dateStr)) return
    this.selectedDates.delete(dateStr)
    this.renderCalendar()
    this.renderChips()
    this.syncHiddenInputs()
  }

  renderCalendar() {
    const monthNames = [
      "January", "February", "March", "April", "May", "June",
      "July", "August", "September", "October", "November", "December"
    ]

    // Render first month
    if (this.monthLabelTargets[0]) {
      this.monthLabelTargets[0].textContent = `${monthNames[this.viewMonth]} ${this.viewYear}`
    }
    if (this.daysGridTargets[0]) {
      this.daysGridTargets[0].innerHTML = this.buildMonthHtml(this.viewYear, this.viewMonth)
    }

    // Render second month (next month) if target exists
    if (this.monthLabelTargets.length > 1 && this.daysGridTargets.length > 1) {
      const nextMonth = (this.viewMonth + 1) % 12
      const nextYear = this.viewMonth === 11 ? this.viewYear + 1 : this.viewYear
      this.monthLabelTargets[1].textContent = `${monthNames[nextMonth]} ${nextYear}`
      this.daysGridTargets[1].innerHTML = this.buildMonthHtml(nextYear, nextMonth)
    }
  }

  buildMonthHtml(year, month) {
    const firstDay = new Date(year, month, 1)
    const lastDay = new Date(year, month + 1, 0)
    const startDow = firstDay.getDay()
    const id = this.actionIdentifier

    let html = ""

    for (let i = 0; i < startDow; i++) {
      html += `<div></div>`
    }

    for (let d = 1; d <= lastDay.getDate(); d++) {
      const date = new Date(year, month, d)
      const dateStr = this.formatDate(date)
      const isSelected = this.selectedDates.has(dateStr)
      const isToday = date.getTime() === this.today.getTime()
      const isPast = date < this.today

      let innerClasses = "flex items-center justify-center w-9 h-9 mx-auto rounded-full text-sm font-medium transition-all "

      if (isPast) {
        innerClasses += "text-gray-300 dark:text-base-600"
        html += `<div class="py-1 text-center"><span class="${innerClasses}">${d}</span></div>`
      } else if (isSelected) {
        innerClasses += "bg-blue-600 text-white font-bold ring-2 ring-blue-600 ring-offset-1 dark:ring-offset-base-900"
        html += `<button type="button" class="py-1 w-full text-center" data-action="${id}#toggleDate" data-date="${dateStr}"><span class="${innerClasses}">${d}</span></button>`
      } else if (isToday) {
        innerClasses += "text-blue-600 dark:text-blue-400 font-bold hover:bg-blue-50 dark:hover:bg-blue-900/30 cursor-pointer"
        html += `<button type="button" class="py-1 w-full text-center" data-action="${id}#toggleDate" data-date="${dateStr}"><span class="${innerClasses}">${d}</span></button>`
      } else {
        innerClasses += "text-gray-700 dark:text-white hover:bg-gray-100 dark:hover:bg-base-700 cursor-pointer"
        html += `<button type="button" class="py-1 w-full text-center" data-action="${id}#toggleDate" data-date="${dateStr}"><span class="${innerClasses}">${d}</span></button>`
      }
    }

    return html
  }

  renderChips() {
    if (!this.hasChipsTarget) return

    const sorted = Array.from(this.selectedDates).sort()
    const id = this.actionIdentifier

    if (sorted.length === 0) {
      this.chipsTarget.innerHTML = ""
      return
    }

    this.chipsTarget.innerHTML = sorted.map(dateStr => {
      const date = new Date(dateStr + "T00:00:00")
      const label = date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
      return `<span class="inline-flex items-center gap-1 bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800 text-blue-700 dark:text-blue-300 text-sm px-3 py-1 rounded-full">
        ${label}
        <button type="button" class="ml-1 hover:text-blue-900 dark:hover:text-blue-100 font-medium leading-none" data-action="${id}#removeDate" data-date="${dateStr}">&times;</button>
      </span>`
    }).join("")
  }

  syncHiddenInputs() {
    if (!this.hasHiddenInputsTarget) return

    const sorted = Array.from(this.selectedDates).sort()
    this.hiddenInputsTarget.innerHTML = sorted.map(dateStr =>
      `<input type="hidden" name="${this.hiddenInputNameValue}" value="${dateStr}">`
    ).join("")
  }

  formatDate(date) {
    const y = date.getFullYear()
    const m = String(date.getMonth() + 1).padStart(2, "0")
    const d = String(date.getDate()).padStart(2, "0")
    return `${y}-${m}-${d}`
  }
}
